import {MinusIcon, PlusIcon} from '@heroicons/react/solid';
import {useState} from 'react';

import {getInvoiceBusinessId} from '@afriex/core/utils';

import {Badge, Button, Table, TBody, Td, Th, Thead, Tr} from '@/components';
import type {AdminBusinessInvoice} from '@/types';
import {invoiceStatusColors} from '@/types/AdminBusinessInvoice';
import {capitalizeWords, formatValues} from '@/utils/dashboard';
import * as formatDate from '@/utils/date';

import InvoiceStatusButton from './InvoiceStatusButton';
import InvoiceSummary from './InvoiceSummary';

const formatEnumLabel = (value: string) =>
  capitalizeWords(value.replace(/_/g, ' '));

const InvoicesTable = ({
  items,
  onInvoiceUpdated,
  dataCy,
}: {
  items: AdminBusinessInvoice[];
  onInvoiceUpdated?: () => void | Promise<void>;
  /** Test hook prefix; each rendered element namespaces under it. */
  dataCy?: string;
}) => (
  <Table
    className="overflow-hidden"
    containerDataCy={dataCy ? `${dataCy}-table` : undefined}
  >
    <Thead>
      <Tr>
        <Th className="w-20">&nbsp;</Th>
        <Th>Invoice</Th>
        <Th>Merchant</Th>
        <Th>Period</Th>
        <Th>Amount</Th>
        <Th>Outstanding</Th>
        <Th>Status</Th>
        <Th>Due date</Th>
        <Th>&nbsp;</Th>
      </Tr>
    </Thead>
    <TBody>
      {items.map((item, index) => (
        <InvoiceRow
          key={item.id}
          item={item}
          index={index}
          onInvoiceUpdated={onInvoiceUpdated}
          dataCy={dataCy}
        />
      ))}
    </TBody>
  </Table>
);

const InvoiceRow = ({
  item,
  index,
  onInvoiceUpdated,
  dataCy,
}: {
  item: AdminBusinessInvoice;
  index: number;
  onInvoiceUpdated?: () => void | Promise<void>;
  dataCy?: string;
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const zebraRow = index % 2 === 0 ? '' : 'bg-gray-50 dark:bg-dark-zebraRow';

  return (
    <>
      {/* The reference is also exposed as an attribute, so a test can pick one
          row out of the page without depending on its position. */}
      <Tr
        className={zebraRow}
        data-cy={dataCy ? `${dataCy}-row` : undefined}
        data-reference={item.meta.reference}
      >
        <Td isNumeric>
          <Button
            onClick={() => setIsExpanded(!isExpanded)}
            variant="unStyled"
            dataCy={dataCy ? `${dataCy}-row-expand` : undefined}
          >
            <div
              className={`border-2 border-gray-700 dark:border-dark-borderLight dark:text-gray-100 text-gray-700 w-5 h-5 flex justify-center rounded hover:bg-cyan-50 ${
                isExpanded && 'bg-cyan-50'
              }`}
            >
              {isExpanded ? (
                <MinusIcon className="w-4 h-4" />
              ) : (
                <PlusIcon className="w-4 h-4" />
              )}
            </div>
          </Button>
        </Td>

        <Td data-cy={dataCy ? `${dataCy}-row-reference` : undefined}>
          <span className="font-medium">{item.meta.reference}</span>
        </Td>

        {/* The list endpoint may not join the merchant name; fall back to the
            id so the column is never blank. */}
        <Td data-cy={dataCy ? `${dataCy}-row-merchant` : undefined}>
          {item.meta.businessName || getInvoiceBusinessId(item) || ''}
        </Td>

        <Td data-cy={dataCy ? `${dataCy}-row-period` : undefined}>
          {formatDate.DDMonYYYY(String(item.dates.periodStart))}
          {' - '}
          {formatDate.DDMonYYYY(String(item.dates.periodEnd))}
        </Td>

        <Td
          className="font-medium"
          data-cy={dataCy ? `${dataCy}-row-amount` : undefined}
        >
          {formatValues(item.amount.value, 'money')} {item.amount.currencyCode}
        </Td>

        {/* amountDue may be absent; derive rather than render a blank, since
            what is still owed is the number personnel scan this column for. */}
        <Td
          className="font-medium"
          data-cy={dataCy ? `${dataCy}-row-outstanding` : undefined}
        >
          {formatValues(
            item.amount.due ?? item.amount.value - (item.amount.paid ?? 0),
            'money',
          )}{' '}
          {item.amount.currencyCode}
        </Td>

        <Td data-cy={dataCy ? `${dataCy}-row-status` : undefined}>
          <Badge
            label={formatEnumLabel(item.meta.status)}
            colorScheme={invoiceStatusColors[item.meta.status]}
          />
        </Td>

        <Td data-cy={dataCy ? `${dataCy}-row-due-date` : undefined}>
          {formatDate.DDMonYYYY(String(item.dates.dueDate))}
        </Td>

        <Td>
          <InvoiceStatusButton
            invoice={item}
            onSuccess={onInvoiceUpdated}
            dataCy={dataCy ? `${dataCy}-row-status` : undefined}
            formDataCy={dataCy ? `${dataCy}-status-form` : undefined}
          />
        </Td>
      </Tr>

      {isExpanded && (
        <Tr enableHover={false}>
          <Td colSpan={9} className="p-0">
            <div className="flex py-10 px-8 justify-center bg-sky-50 dark:bg-transparent w-full">
              <InvoiceSummary
                item={item}
                dataCy={dataCy ? `${dataCy}-summary` : undefined}
              />
            </div>
          </Td>
        </Tr>
      )}
    </>
  );
};

export default InvoicesTable;
